/**
 * SignalsContext
 * Keeps WebSocket connection and signal/arbiter state alive across route changes
 * so that when the user leaves Tradar and comes back, they still see their
 * signals and continue to receive new ones.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "@clerk/clerk-react";
import { Signal } from "../views/Dashboard/Tables/components/SignalCard";
import { Trade } from "../types/trade";
import {
  SystemPerformanceData,
  AccountUpdateData,
  TradeFplUpdateItem,
  SlTpUpdateItem,
} from "../types/signals";
import type { MarketOverviewLatest } from "../types/marketOverview";
import { formatInstrumentSymbol } from "../services/signalMapper";
import { useSignals } from "../hooks/useSignals";
import { useSignalWebSocket } from "../hooks/useSignalWebSocket";
import { getSystemPerformance } from "../services/signalsApi";
import { getAccountSnapshot } from "../services/liquidBrokersApi";

const DEFAULT_SYMBOL = "XAU/USD";

export interface SignalsContextValue {
  /** Signals per asset symbol; persists across navigation */
  assetSignals: Record<string, Signal[]>;
  /** Latest no_signal reason from WebSocket */
  latestNoSignalReason: string | null;
  /** System performance (API + WebSocket) */
  systemPerformance: SystemPerformanceData | null;
  /** Fetch system performance from API (call when Tradar page loads); WebSocket updates when setups enabled */
  loadSystemPerformance: () => Promise<void>;
  /** Per-account metrics from account_update WebSocket events */
  accountMetrics: Record<string, AccountUpdateData>;
  /** Taken setups (trades created from Tradar signals), keyed by trade ID */
  takenTrades: Record<string, (Trade & { floatingPnl?: number })>;
  /** Seed or update taken trades from REST responses */
  upsertTakenTrades: (trades: Trade[]) => void;
  /** Replace taken trades with only these (e.g. after refetch); preserves floatingPnl where present */
  replaceWithOpenTrades: (trades: Trade[]) => void;
  /** Clear all taken trades (e.g. on account switch before refetch). */
  clearTakenTrades: () => void;
  /** Remove taken trades by ID (e.g. on trade_closed) */
  removeTakenTrades: (ids: string[]) => void;
  /** Remove a signal (e.g. when user takes the trade) */
  removeSignal: (symbol: string, signalId: string) => void;
  /** Whether signals are enabled (from useSignals) */
  signalsEnabled: boolean;
  /** Time remaining (seconds) that signals will stay enabled; null when disabled */
  timeRemainingSeconds: number | null;
  /** Loading state for enable/disable actions */
  signalsLoading: boolean;
  /** Enable signals (starts WebSocket streaming on backend) */
  enableSignals: () => Promise<void>;
  /** Disable signals (stops WebSocket streaming on backend) */
  disableSignals: () => Promise<void>;
  /** Format remaining time for UI display */
  formatTimeRemaining: (seconds: number | null) => string;
  /** WebSocket connection state – use to show status and ensure connection */
  wsConnection: {
    isConnected: boolean;
    isConnecting: boolean;
    error: string | null;
    /** Call to trigger connect (e.g. after enabling signals or to retry) */
    reconnect: () => void;
  };
  /** Re-fetch signals status from API; use when opening Tradar so WebSocket can start if backend has signals enabled */
  ensureSignalsStatus: () => Promise<void>;
  /** Fetch account snapshot when broker_name is set and broker supports live sync. */
  loadAccountSnapshot: (options?: { refresh?: boolean; broker_name?: string }) => Promise<void>;
  /** Latest market overview per instrument (from WebSocket market_overview); key is display symbol e.g. "XAU/USD" */
  marketOverviewByInstrument: Record<string, MarketOverviewLatest>;
}

const SignalsContext = createContext<SignalsContextValue | null>(null);

export function useSignalsContext(): SignalsContextValue {
  const ctx = useContext(SignalsContext);
  if (!ctx) {
    throw new Error("useSignalsContext must be used within SignalsProvider");
  }
  return ctx;
}

/** Optional hook for components that might not be under the provider (e.g. outside /admin) */
export function useSignalsContextOptional(): SignalsContextValue | null {
  return useContext(SignalsContext);
}

interface SignalsProviderProps {
  children: React.ReactNode;
}

export function SignalsProvider({ children }: SignalsProviderProps) {
  const { isLoaded: isAuthLoaded, isSignedIn, getToken } = useAuth();
  const {
    signalsEnabled,
    timeRemainingSeconds,
    isLoading: signalsLoading,
    enableSignals,
    disableSignals,
    checkStatus,
    formatTimeRemaining,
    websocketPath,
  } = useSignals();
  const [assetSignals, setAssetSignals] = useState<Record<string, Signal[]>>({});
  const [latestNoSignalReason, setLatestNoSignalReason] = useState<string | null>(null);
  const [systemPerformance, setSystemPerformance] = useState<SystemPerformanceData | null>(null);
  const [accountMetrics, setAccountMetrics] = useState<Record<string, AccountUpdateData>>({});
  const [takenTrades, setTakenTrades] = useState<Record<string, Trade & { floatingPnl?: number }>>({});
  const [marketOverviewByInstrument, setMarketOverviewByInstrument] = useState<Record<string, MarketOverviewLatest>>({});

  const handleNoSignalReceived = useCallback((reason: string) => {
    setLatestNoSignalReason(reason);
  }, []);

  const handleSignalReceived = useCallback((signal: Signal) => {
    const targetSymbol = signal.symbol ?? DEFAULT_SYMBOL;
    let isNewSignal = false;
    setAssetSignals((prev) => {
      const current = prev[targetSymbol] || [];
      if (current.some((s) => s.id === signal.id)) return prev;
      isNewSignal = true;
      const updated = [...current, signal].sort((a, b) => {
        const tA = new Date(a.expiryTime).getTime();
        const tB = new Date(b.expiryTime).getTime();
        return tB - tA;
      });
      return { ...prev, [targetSymbol]: updated };
    });
    if (!isNewSignal) return;
  }, []);

  const handleSystemPerformance = useCallback((data: SystemPerformanceData) => {
    setSystemPerformance(data);
  }, []);

  const handleAccountUpdate = useCallback((data: AccountUpdateData) => {
    const key = data.account_code || "__latest";
    // Only update account metrics when we have balance/unrealized_pnl so we don't overwrite with sl_tp_only payloads
    const hasMetricFields = typeof data.unrealized_pnl === "number" || data.balance != null;
    if (hasMetricFields) {
      setAccountMetrics((prev) => ({
        ...prev,
        [key]: { ...data, account_code: data.account_code || key },
      }));
    }

    // Trim stale open rows for this account when broker sends a non-empty authoritative set.
    if (
      data.open_trade_ids &&
      Array.isArray(data.open_trade_ids) &&
      data.open_trade_ids.length > 0 &&
      (data.account_code || key)
    ) {
      const accountCode = data.account_code || key;
      const openSet = new Set(data.open_trade_ids);
      setTakenTrades((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((tradeId) => {
          const trade = next[tradeId];
          const tradeAccount = (trade as Trade & { brokerAccountCode?: string }).brokerAccountCode;
          if (tradeAccount === accountCode && !openSet.has(tradeId)) {
            delete next[tradeId];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    // Merge open_trades_unrealized into takenTrades so Active Setups table shows FPL per trade
    if (data.open_trades_unrealized && Array.isArray(data.open_trades_unrealized) && data.open_trades_unrealized.length > 0) {
      setTakenTrades((prev) => {
        let changed = false;
        const next = { ...prev };
        data.open_trades_unrealized!.forEach((u) => {
          const existing = next[u.trade_id];
          if (!existing) return;
          next[u.trade_id] = { ...existing, floatingPnl: u.fpl };
          changed = true;
        });
        return changed ? next : prev;
      });
    }

    // Merge sl_tp_updates into takenTrades so Active Setups table shows up-to-date loss_potential / profit_potential
    if (data.sl_tp_updates && Array.isArray(data.sl_tp_updates) && data.sl_tp_updates.length > 0) {
      setTakenTrades((prev) => {
        let changed = false;
        const next = { ...prev };
        (data.sl_tp_updates as SlTpUpdateItem[]).forEach((u) => {
          const existing = next[u.trade_id];
          if (!existing) return;
          const updates: Partial<Trade & { floatingPnl?: number }> = {};
          if (u.stop_loss_price !== undefined) updates.stopLoss = u.stop_loss_price ?? undefined;
          if (u.take_profit_price !== undefined) updates.takeProfit = u.take_profit_price ?? undefined;
          if (u.loss_potential !== undefined) updates.lossPotential = u.loss_potential ?? undefined;
          if (u.profit_potential !== undefined) updates.profitPotential = u.profit_potential ?? undefined;
          if (Object.keys(updates).length === 0) return;
          next[u.trade_id] = { ...existing, ...updates };
          changed = true;
        });
        return changed ? next : prev;
      });
    }
  }, []);

  const handleTradeFplUpdate = useCallback((updates: TradeFplUpdateItem[]) => {
    if (!updates || updates.length === 0) return;

    // Only patch FPL onto open trades we already know (snapshot / Trade Log seed).
    // Do not GET /trades/{id} per unknown id — WS ticks would hammer the API.
    setTakenTrades((prev) => {
      let changed = false;
      const next = { ...prev };
      updates.forEach((u) => {
        const existing = next[u.trade_id];
        if (!existing) return;
        next[u.trade_id] = {
          ...existing,
          floatingPnl: u.fpl,
        };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, []);

  const handleTradeClosed = useCallback((tradeIds: string[]) => {
    if (!tradeIds || tradeIds.length === 0) return;
    setTakenTrades((prev) => {
      const next = { ...prev };
      tradeIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  }, []);

  const handleMarketOverview = useCallback((data: MarketOverviewLatest) => {
    const key = formatInstrumentSymbol(data.instrument);
    setMarketOverviewByInstrument((prev) => ({ ...prev, [key]: data }));
  }, []);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!isSignedIn) return null;
    try {
      return (await getToken()) ?? null;
    } catch (error) {
      console.error("[signals] Failed to get Clerk token for WebSocket auth:", error);
      return null;
    }
  }, [getToken, isSignedIn]);

  const {
    isConnected,
    isConnecting,
    error: wsError,
    connect,
  } = useSignalWebSocket({
    onSignalReceived: handleSignalReceived,
    onNoSignalReceived: handleNoSignalReceived,
    onArbiterPerformance: handleSystemPerformance,
    onAccountUpdate: handleAccountUpdate,
    onTradeFplUpdate: handleTradeFplUpdate,
    onTradeClosed: handleTradeClosed,
    onMarketOverview: handleMarketOverview,
    // Keep one persistent /ws/signals session for Tradar + report_ready + notifications.
    authLoaded: isAuthLoaded,
    signedIn: isSignedIn,
    websocketPath,
    getAuthToken,
  });

  const ensureSignalsStatus = useCallback(async () => {
    await checkStatus();
  }, [checkStatus]);

  const loadSystemPerformance = useCallback(async () => {
    try {
      const data = await getSystemPerformance();
      setSystemPerformance(data);
    } catch (err) {
      console.error("Failed to load system performance:", err);
    }
  }, []);

  const loadAccountSnapshot = useCallback(
    async (options?: { refresh?: boolean; broker_name?: string }) => {
      const brokerName = options?.broker_name?.trim();
      if (!brokerName) {
        return;
      }
      try {
        const snapshot = await getAccountSnapshot({
          refresh: options?.refresh ?? true,
          broker_name: brokerName,
        });
        const normalized: AccountUpdateData = {
          account_code: snapshot.account_code,
          balance: snapshot.balance ?? 0,
          equity: snapshot.equity ?? 0,
          portfolio_balance: snapshot.portfolio_balance ?? 0,
          unrealized_pnl: snapshot.unrealized_pnl ?? 0,
          account_risk_pct: snapshot.account_risk_pct,
          todays_pnl: snapshot.todays_pnl,
          portfolio_change_percent: snapshot.portfolio_change_percent,
          timestamp: snapshot.timestamp,
          open_trade_ids: snapshot.open_trade_ids,
          open_trades_unrealized: snapshot.open_trades_unrealized ?? [],
        };
        const key = snapshot.account_code || "__latest";
        setAccountMetrics((prev) => ({
          ...prev,
          [key]: normalized,
        }));

        // Merge open_trades_unrealized into takenTrades so Active Setups shows FPL per trade
        const unrealized = snapshot.open_trades_unrealized ?? [];
        if (unrealized.length > 0) {
          setTakenTrades((prev) => {
            let changed = false;
            const next = { ...prev };
            unrealized.forEach((u) => {
              const existing = next[u.trade_id];
              if (!existing) return;
              next[u.trade_id] = { ...existing, floatingPnl: u.fpl };
              changed = true;
            });
            return changed ? next : prev;
          });
        }
      } catch (err) {
        console.error("Failed to load account snapshot:", err);
      }
    },
    []
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setAssetSignals((prev) => {
        const updated: Record<string, Signal[]> = {};
        const now = new Date();
        Object.keys(prev).forEach((symbol) => {
          const active = prev[symbol].filter((s) => new Date(s.expiryTime) > now);
          active.sort((a, b) => {
            const tA = new Date(a.expiryTime).getTime();
            const tB = new Date(b.expiryTime).getTime();
            return tB - tA;
          });
          updated[symbol] = active;
        });
        return updated;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const removeSignal = useCallback((symbol: string, signalId: string) => {
    setAssetSignals((prev) => {
      const list = prev[symbol] || [];
      const next = list.filter((s) => s.id !== signalId);
      if (next.length === 0 && list.length > 0) {
        const { [symbol]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [symbol]: next };
    });
  }, []);

  const upsertTakenTrades = useCallback((trades: Trade[]) => {
    if (!trades || trades.length === 0) return;
    setTakenTrades((prev) => {
      const next = { ...prev };
      trades.forEach((t) => {
        const existing = next[t.id];
        next[t.id] = {
          ...t,
          // Preserve existing floatingPnl if present
          floatingPnl: existing?.floatingPnl,
        };
      });
      return next;
    });
  }, []);

  const replaceWithOpenTrades = useCallback((trades: Trade[]) => {
    if (!trades || trades.length === 0) {
      return;
    }
    setTakenTrades((prev) => {
      const next: Record<string, Trade & { floatingPnl?: number }> = {};
      trades.forEach((t) => {
        next[t.id] = {
          ...t,
          floatingPnl: prev[t.id]?.floatingPnl,
        };
      });
      return next;
    });
  }, []);

  const clearTakenTrades = useCallback(() => {
    setTakenTrades({});
  }, []);

  const removeTakenTrades = useCallback((ids: string[]) => {
    if (!ids || ids.length === 0) return;
    setTakenTrades((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  }, []);

  const value: SignalsContextValue = {
    assetSignals,
    latestNoSignalReason,
    systemPerformance,
    loadSystemPerformance,
    loadAccountSnapshot,
    accountMetrics,
    marketOverviewByInstrument,
    takenTrades,
    upsertTakenTrades,
    replaceWithOpenTrades,
    clearTakenTrades,
    removeTakenTrades,
    removeSignal,
    signalsEnabled,
    timeRemainingSeconds,
    signalsLoading,
    enableSignals,
    disableSignals,
    formatTimeRemaining,
    wsConnection: {
      isConnected,
      isConnecting,
      error: wsError,
      reconnect: connect,
    },
    ensureSignalsStatus,
  };

  return (
    <SignalsContext.Provider value={value}>
      {children}
    </SignalsContext.Provider>
  );
}
